import React, { useState, useContext } from "react";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Button from "@mui/material/Button";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useSnackbar } from "notistack";
import PulseLoader from "react-spinners/PulseLoader";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Paper } from "@mui/material";
import { postDataWithToken } from "../../services/PostDataService";
import WebsiteHeader from "../../website-header/WebsiteHeader";
import WebsiteFooter from "../../website-footer/WebsiteFooter";

const useStyles = makeStyles((theme) => ({
  form: {
    padding: "80px 100px",
    textAlign: "center",
    width: "550px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      width: "100% !important",
      padding: "40px 40px",
    },
  },
  customInput: {
    "& label.Mui-focused": {
      color: "#A0AAB4",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "none",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#E0E3E7",
      },
      "&:hover fieldset": {
        borderColor: "none",
      },
      "&.Mui-focused fieldset": {
        borderColor: "none",
        border: "none",
      },
    },
  },
  input: {
    background: "#E5FFFD",
    borderRadius: "4px",
    "& fieldset": { border: "none" },
    input: {
      color: "#000",
      fontWeight: 500,
    },
  },
  button: {
    borderRadius: 0,
    marginTop: 16,
  },
  registerButton: {
    borderRadius: 0,
    marginTop: 16,
    borderColor: "#016363",
    color: "#016363",
    "&:hover": {
      borderColor: "#016363",
      backgroundColor: "rgba(1, 99, 99, 0.04)",
    },
  },
}));

const Register = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();

  const { admin_panel_user, logout } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: OTP, 3: Complete
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    firstName: "",
    lastName: "",
    fullName: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company: "",
    agreeToTerms: false,
    token: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleCheckboxChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.checked,
    });
  };

  const validateEmailStep = () => {
    if (!formData.email.trim()) {
      handleSnackbarOpen("Please enter email address", "error");
      document.getElementById("email").focus();
      return false;
    } else if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        formData.email.trim()
      )
    ) {
      handleSnackbarOpen("Invalid email address", "error");
      document.getElementById("email").focus();
      return false;
    }
    return true;
  };

  const validateOtpStep = () => {
    if (!formData.otp.trim()) {
      handleSnackbarOpen("Please enter the OTP", "error");
      document.getElementById("otp").focus();
      return false;
    } else if (formData.otp.length !== 6) {
      handleSnackbarOpen("OTP must be 6 digits", "error");
      document.getElementById("otp").focus();
      return false;
    }
    return true;
  };

  const validateCompleteStep = () => {
    let isError = false;

    if (!formData.fullName.trim()) {
      handleSnackbarOpen("Please enter your full name", "error");
      document.getElementById("fullName").focus();
      return (isError = true);
    }

    if (!formData.password.trim()) {
      handleSnackbarOpen("Please enter password", "error");
      document.getElementById("password").focus();
      return (isError = true);
    } else if (formData.password.length < 6) {
      handleSnackbarOpen("Password must be at least 6 characters", "error");
      document.getElementById("password").focus();
      return (isError = true);
    }

    if (formData.password !== formData.confirmPassword) {
      handleSnackbarOpen("Passwords do not match", "error");
      document.getElementById("confirmPassword").focus();
      return (isError = true);
    }

    if (!formData.agreeToTerms) {
      handleSnackbarOpen("Please agree to the terms and conditions", "error");
      return (isError = true);
    }

    return isError;
  };

  const handleSnackbarOpen = (msg, variant) => {
    let duration;
    if (variant === "error") {
      duration = 3000;
    } else {
      duration = 1000;
    }
    enqueueSnackbar(msg, {
      variant: variant,
      autoHideDuration: duration,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailStep()) return;

    setLoading(true);
    try {
      const response = await postDataWithToken(
        "signUp",
        { email: formData.email.trim() },
        admin_panel_user,
        logout
      );
      
      if (response.data.code === 200) {
        handleSnackbarOpen(response.data.message, "success");
        setCurrentStep(2);
      } else {
        handleSnackbarOpen(response.data.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      console.error("Email submission error:", error);
      handleSnackbarOpen("Failed to send OTP. Please try again.", "error");
    }
    setLoading(false);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!validateOtpStep()) return;

    setLoading(true);
    try {
      const response = await postDataWithToken(
        "signUpOtpVerificaiton",
        { 
          email: formData.email.trim(),
          otp: formData.otp.trim()
        },
        admin_panel_user,
        logout
      );
      
      if (response.data.code === 200) {
        handleSnackbarOpen(response.data.message, "success");
        setFormData(prev => ({ ...prev, token: response.data.data.token }));
        setCurrentStep(3);
      } else {
        handleSnackbarOpen(response.data.message || "Invalid OTP", "error");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      handleSnackbarOpen("OTP verification failed. Please try again.", "error");
    }
    setLoading(false);
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    let err = validateCompleteStep();
    if (err) return;

    setLoading(true);
    try {
      const response = await postDataWithToken(
        "signUpComplete",
        {
          email: formData.email.trim(),
          token: formData.token,
          name: formData.fullName.trim(),
          password: formData.password.trim()
        },
        admin_panel_user,
        logout
      );
      
      if (response.data.code === 200) {
        handleSnackbarOpen("Registration successful! Please login to continue.", "success");
        navigate("/");
      } else {
        handleSnackbarOpen(response.data.message || "Registration failed", "error");
      }
    } catch (error) {
      console.error("Registration completion error:", error);
      handleSnackbarOpen("Registration failed. Please try again.", "error");
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#e7fffd",
        }}
      >
        <WebsiteHeader />
        <Paper sx={{ width: { xs: "90%", sm: "auto" } }}>
          <form 
            className={classes.form} 
            onSubmit={
              currentStep === 1 ? handleEmailSubmit :
              currentStep === 2 ? handleOtpSubmit :
              handleCompleteSubmit
            }
          >
            <img
              src={
                theme.palette.mode === "light"
                  ? "/images/Logo.png"
                  : "/images/Logo_Dark.png"
              }
              alt=""
              style={{ display: "block", margin: "auto" }}
            />
            <br />
            <Typography
              variant="h5"
              component="div"
              style={{ marginBottom: "20px", color: "#016363" }}
            >
              {currentStep === 1 && "Create Your Account"}
              {currentStep === 2 && "Verify Your Email"}
              {currentStep === 3 && "Complete Registration"}
            </Typography>
            {/* Step 1: Email Input */}
            {currentStep === 1 && (
              <>
                <TextField
                  placeholder="Enter your email address"
                  fullWidth
                  style={{ marginBottom: "20px" }}
                  InputProps={{
                    className: classes.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                />

                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  style={{ marginBottom: "20px", minHeight: "55px" }}
                  disabled={loading}
                  type="submit"
                  className={classes.button}
                >
                  {loading === false && "Send OTP"}
                  <PulseLoader
                    color={theme.palette.primary.main}
                    loading={loading}
                    size={10}
                    speedMultiplier={0.5}
                  />
                </Button>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {currentStep === 2 && (
              <>
                <Typography variant="body2" style={{ marginBottom: "20px", color: "#666" }}>
                  We've sent a 6-digit OTP to {formData.email}
                </Typography>

                <TextField
                  placeholder="Enter 6-digit OTP"
                  fullWidth
                  style={{ marginBottom: "20px" }}
                  InputProps={{
                    className: classes.input,
                    inputProps: { maxLength: 6, style: { textAlign: "center", fontSize: "18px", letterSpacing: "2px" } }
                  }}
                  variant="outlined"
                  id="otp"
                  value={formData.otp}
                  onChange={handleInputChange("otp")}
                />

                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  style={{ marginBottom: "20px", minHeight: "55px" }}
                  disabled={loading}
                  type="submit"
                  className={classes.button}
                >
                  {loading === false && "Verify OTP"}
                  <PulseLoader
                    color={theme.palette.primary.main}
                    loading={loading}
                    size={10}
                    speedMultiplier={0.5}
                  />
                </Button>

                <Button
                  variant="text"
                  style={{ color: "#016363" }}
                  onClick={() => setCurrentStep(1)}
                >
                  Change Email Address
                </Button>
              </>
            )}

            {/* Step 3: Complete Registration */}
            {currentStep === 3 && (
              <>
                <TextField
                  placeholder="Full Name"
                  fullWidth
                  style={{ marginBottom: "20px" }}
                  InputProps={{
                    className: classes.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => {
                    const fullName = e.target.value;
                    const nameParts = fullName.split(' ');
                    setFormData(prev => ({
                      ...prev,
                      fullName: fullName,
                      firstName: nameParts[0] || '',
                      lastName: nameParts.slice(1).join(' ') || ''
                    }));
                  }}
                  inputProps={{
                    maxLength: 100
                  }}
                />

                <FormControl
                  fullWidth
                  variant="outlined"
                  style={{ marginBottom: "20px" }}
                  className={classes.input}
                >
                  <OutlinedInput
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange("password")}
                    startAdornment={
                      <InputAdornment position="start">
                        <LockOutlinedIcon />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  variant="outlined"
                  style={{ marginBottom: "20px" }}
                  className={classes.input}
                >
                  <OutlinedInput
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange("confirmPassword")}
                    startAdornment={
                      <InputAdornment position="start">
                        <LockOutlinedIcon />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>

                <FormGroup style={{ marginBottom: "20px" }}>
                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      marginLeft: 0,
                      "& .MuiCheckbox-root": { paddingTop: 0.25, paddingBottom: 0 },
                      "& .MuiFormControlLabel-label": { marginTop: "2px" },
                    }}
                    control={
                      <Checkbox 
                        checked={formData.agreeToTerms}
                        onChange={handleCheckboxChange("agreeToTerms")}
                      />
                    }
                    label={
                      <Typography variant="body2" style={{ fontSize: "14px", lineHeight: 1.5 }}>
                        I agree to the{" "}
                        <a 
                          href="https://www.certifymyid.com/terms-of-use" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: "#016363", 
                            textDecoration: "none",
                            cursor: "pointer"
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                          onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                        >
                          Terms and Conditions
                        </a>
                      </Typography>
                    }
                  />
                </FormGroup>

                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  style={{ marginBottom: "20px", minHeight: "55px" }}
                  disabled={loading}
                  type="submit"
                  className={classes.button}
                >
                  {loading === false && "Create Account"}
                  <PulseLoader
                    color={theme.palette.primary.main}
                    loading={loading}
                    size={10}
                    speedMultiplier={0.5}
                  />
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              disableElevation
              fullWidth
              style={{ 
                marginBottom: "30px", 
                minHeight: "55px",
                borderColor: "#016363",
                color: "#016363",
              }}
              onClick={() => navigate("/")}
              className={classes.registerButton}
            >
              Already have an account? Sign In
            </Button>
          </form>
        </Paper>
        <WebsiteFooter />
      </div>
    </React.Fragment>
  );
};

export default Register;
